import { type CalloutType, Callout } from "@octopusdeploy/design-system-components";
import { LicenseMessageDisposition } from "@octopusdeploy/octopus-server-client";
import { OctoLink } from "@octopusdeploy/utilities";
import { useAnalyticLicenseUsageDispatch } from "~/analytics/Analytics";
import { GetLicenseExpirationUrgency } from "~/components/Navbar/LicenseCountdownBanner";
import DateFormatter from "~/utils/DateFormatter";
interface LicenseCalloutProps {
    serialNumber: string;
    effectiveExpiryDate: string;
    daysToEffectiveExpiryDate: number;
    licenseDisposition: LicenseMessageDisposition | undefined;
}
export function LicenseCallout({ serialNumber, effectiveExpiryDate, daysToEffectiveExpiryDate, licenseDisposition }: LicenseCalloutProps) {
    const licenseExpirationUrgency = GetLicenseExpirationUrgency(licenseDisposition);
    const dispatchAction = useAnalyticLicenseUsageDispatch();
    const supportEmail = "support@octopus.com";
    const contactUs = (<>
            <br /> If you need help or have a question, email us at{" "}
            <b>
                <a href={`mailto:${supportEmail}`} aria-label={supportEmail}>
                    {supportEmail}
                </a>
            </b>
            . We're here to help.
        </>);
    const manageSubscriptionClicked = () => {
        dispatchAction("Click Buy Now in Control Center", { urgency: licenseExpirationUrgency, resource: "License" });
    };
    const getCalloutData = (): {
        calloutTitle: string;
        calloutType: CalloutType;
        calloutContent: JSX.Element;
    } => {
        let calloutTitle = "We hope you're enjoying your Octopus Deploy Enterprise trial";
        let calloutType: CalloutType = "information";
        let calloutContent = (<>
                To continue using Octopus after your trial ends on <b>{DateFormatter.dateToCustomFormat(effectiveExpiryDate, "MMMM DD")}</b>, please purchase a subscription.{contactUs}
            </>);
        if (licenseDisposition === LicenseMessageDisposition.Notice) {
            calloutTitle = "Your Enterprise trial ends soon";
            calloutType = "warning";
            calloutContent = (<>
                    We hope you're enjoying your Octopus Deploy Enterprise trial. To continue using Octopus after your trial ends on <b>{DateFormatter.dateToCustomFormat(effectiveExpiryDate, "MMMM DD")}</b>, please purchase a subscription.{contactUs}
                </>);
        }
        else if (licenseDisposition === LicenseMessageDisposition.Warning && daysToEffectiveExpiryDate === 0) {
            calloutTitle = "Your Enterprise trial ends today";
            calloutType = "danger";
            calloutContent = <>We hope you enjoyed your Octopus Deploy Enterprise trial. To continue using Octopus, please purchase a subscription.{contactUs}</>;
        }
        else if ((licenseDisposition === LicenseMessageDisposition.Warning || licenseDisposition === LicenseMessageDisposition.Error) && daysToEffectiveExpiryDate < 0) {
            calloutTitle = "Your Enterprise trial has ended";
            calloutType = "danger";
            calloutContent = <>We hope you enjoyed your Octopus Deploy Enterprise trial. To continue using Octopus, please purchase a subscription. Otherwise, access to this instance will end shortly.{contactUs}</>;
        }
        return { calloutTitle, calloutType, calloutContent };
    };
    const { calloutTitle, calloutType, calloutContent } = getCalloutData();
    return (<Callout title={calloutTitle} type={calloutType} actions={{
            primary: {
                label: "Manage Subscription",
                href: OctoLink.Create(`plan-upgrade?serial=${serialNumber}`),
                external: true,
                onClick: manageSubscriptionClicked,
            },
        }}>
            {calloutContent}
        </Callout>);
}
